import { useMemo } from "react";

import { FinancialAccountFragment } from "@/__generated__/graphql";
import { DataView } from "@/components/organisms/DataView";
import { useConnectionSetup } from "@/hooks/useConnectionSetup";
import { Objects } from "@/objects";
import { State } from "@/state";

import {
  financialAccountToThreadContext,
  FinancialAccountsViewThreadActions,
} from "./FinancialAccountsViewThreadActions";
import { usePrepareDataViewThread } from "../shared/dataViewThread.tools";

export function FinancialAccountsView() {
  const dataViewMode = State(
    (store) => store.dataViewModes[store.activeAppView] ?? "table",
  );

  const {
    columns,
    rows,
    loading,
    totalCount,
    deleteNodeButtonProps,
    loadNext,
    networkStatus,
  } = useConnectionSetup({ appView: "financial-accounts" });

  const financialAccountRows = rows as FinancialAccountFragment[];
  const prepareThread = usePrepareDataViewThread(
    financialAccountRows,
    financialAccountToThreadContext,
  );

  const onCheckedRowsExtraActions = useMemo(
    () => (props: { checkedRowIds: string[]; clearSelection: () => void }) => (
      <FinancialAccountsViewThreadActions
        {...props}
        onPrepareThread={prepareThread}
      />
    ),
    [prepareThread],
  );

  return (
    <DataView
      totalCount={totalCount}
      columns={columns}
      rows={financialAccountRows}
      loadNext={loadNext}
      isLoading={loading}
      networkStatus={networkStatus}
      onCheckedRowsExtraActions={onCheckedRowsExtraActions}
      onCheckedRowsButtonsProps={[deleteNodeButtonProps]}
      viewMode={dataViewMode}
      onAddNew={() =>
        State.getState().pushToCommandStack({
          commandDefinitionId: "add-bank-account",
        })
      }
      placeholderRow={Objects.FinancialAccountObject.placeholder}
      emptyState={{
        ReactIcon: Objects.FinancialAccountObject.ReactIcon,
        title: "No accounts connected",
        description:
          "Connect a bank or credit card account to bring its transactions into the ledger.",
        actionLabel: "Connect an account",
      }}
    />
  );
}
